import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/router"

import { logger } from "@/lib/logger"
import { buildWorkspaceAwareNavigation } from "@/utils/workspaceRoutes"

import {
  deserializeFromStorage,
  getOwnedUrlKeys,
  parseUrlParams,
  serializeForStorage,
  serializeToUrlParams,
} from "./serialization"
import type { FilterParamSchema, FilterParamsResult } from "./types"

const log = logger("FilterParams")

interface UseFilterParamsConfig<T extends object> {
  /** Schema defining each field's type and optional URL key */
  schema: FilterParamSchema<T>
  /** Default values when nothing is in the URL or localStorage */
  defaults: T
  /** localStorage key for resumability. Omit to disable persistence. */
  storageKey?: string
  /** Debounce interval (ms) for URL updates via router.replace (default 300) */
  debounceMs?: number
}

/**
 * Manages filter state with URL query params as the source of truth
 * and optional localStorage fallback for resumability.
 *
 * Initialization priority:
 *   1. URL params (shared link) — always win
 *   2. localStorage (resumability) — restored then synced to URL
 *   3. defaults — clean start
 *
 * URL updates use `router.replace` (shallow, no scroll) to avoid
 * polluting browser history. Non-owned query params (e.g. `agent_id`
 * from `useQueryParams`) are preserved on every sync.
 *
 * **Use this for all filter/facet state.** For imperative reads,
 * navigation, or one-shot param writes, use `useQueryParams` instead.
 * Do not write the same URL key from both hooks on one page.
 */

export function useFilterParams<T extends object>({
  schema,
  defaults,
  storageKey,
  debounceMs = 300,
}: UseFilterParamsConfig<T>): FilterParamsResult<T> {
  const router = useRouter()

  // Refs for stable values that shouldn't trigger re-renders
  const routerRef = useRef(router)
  routerRef.current = router

  const schemaRef = useRef(schema)
  const defaultsRef = useRef(defaults)
  const debounceMsRef = useRef(debounceMs)

  // storageKey can change at runtime — it is typically scoped by org/workspace
  // id (see useScopedStorageKey), and the active org/workspace can change
  // without this hook's owner unmounting (e.g. a client-side workspace
  // switch that stays on the same page). Keep the ref current every render,
  // unlike schemaRef/defaultsRef above, so saves/resets always target the
  // active scope rather than whatever scope was active on first mount.
  const storageKeyRef = useRef(storageKey)
  storageKeyRef.current = storageKey

  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasInitializedRef = useRef(false)
  // The storageKey that `state` was last hydrated for, so a scope change
  // (not just first mount) re-triggers hydration under the new key.
  const initializedForKeyRef = useRef<string | undefined>(undefined)
  const [isReady, setIsReady] = useState(false)

  // ── Derived / memoized ────────────────────────────────────────────

  const routeParamNames = useMemo(() => {
    const matches = router.pathname.match(/\[([^\]]+)\]/g)
    return new Set(matches?.map((m) => m.slice(1, -1)) ?? [])
  }, [router.pathname])

  const ownedUrlKeys = useMemo(
    () => getOwnedUrlKeys(schemaRef.current),
    [], // schema is treated as stable
  )

  // ── State ─────────────────────────────────────────────────────────

  const hydrateFromStorage = useCallback((key: string | undefined): T => {
    if (typeof window === "undefined" || !key) {
      return defaultsRef.current
    }
    try {
      const stored = localStorage.getItem(key)
      if (stored) {
        return deserializeFromStorage(
          stored,
          schemaRef.current,
          defaultsRef.current,
        )
      }
    } catch (error: unknown) {
      log.warn("Failed to read filter state from localStorage", { error })
    }
    return defaultsRef.current
  }, [])

  // Synchronously hydrate from localStorage to avoid a flash of empty filters.
  // URL params (if any) override this in the initialization effect.
  const [state, setState] = useState<T>(() => hydrateFromStorage(storageKey))

  // ── URL sync helpers ──────────────────────────────────────────────

  // Build a query object that preserves route params and non-owned query params
  const buildQuery = useCallback(
    (serializedParams: Record<string, string>) => {
      const r = routerRef.current
      const query: Record<string, string | string[]> = {}

      // 1. Route params (slug, run_id, etc.)
      routeParamNames.forEach((name) => {
        const val = r.query[name]
        if (val !== undefined) {
          query[name] = val
        }
      })

      // 2. Non-owned query params (e.g. agent_id from useAgentFilter)
      for (const [key, val] of Object.entries(r.query)) {
        if (
          !routeParamNames.has(key) &&
          !ownedUrlKeys.has(key) &&
          val !== undefined
        ) {
          query[key] = val
        }
      }

      // 3. Our serialized params (empty-valued keys are simply absent)
      for (const [key, val] of Object.entries(serializedParams)) {
        query[key] = val
      }

      return query
    },
    [routeParamNames, ownedUrlKeys],
  )

  const doSyncToUrl = useCallback(
    (newState: T, removeUrlKeys: string[] = []) => {
      try {
        const r = routerRef.current
        const params = serializeToUrlParams(newState, schemaRef.current)
        const query = buildQuery(params)
        for (const key of removeUrlKeys) {
          delete query[key]
        }

        const navigation = buildWorkspaceAwareNavigation(
          r.asPath,
          r.pathname,
          query,
        )

        void r.replace(navigation.href, navigation.as, {
          scroll: false,
          shallow: true,
        })
      } catch (error: unknown) {
        log.warn("Failed to sync filter state to URL", { error })
      }
    },
    [buildQuery],
  )

  const syncToUrl = useCallback(
    (newState: T, immediate = false, removeUrlKeys: string[] = []) => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }

      if (immediate) {
        doSyncToUrl(newState, removeUrlKeys)
      } else {
        syncTimeoutRef.current = setTimeout(
          () => doSyncToUrl(newState),
          debounceMsRef.current,
        )
      }
    },
    [doSyncToUrl],
  )

  // ── localStorage helpers ──────────────────────────────────────────

  const saveToStorage = useCallback((newState: T) => {
    const key = storageKeyRef.current
    if (!key || typeof window === "undefined") {
      return
    }
    try {
      localStorage.setItem(
        key,
        serializeForStorage(newState, schemaRef.current),
      )
    } catch (error: unknown) {
      log.warn("Failed to save filter state to localStorage", { error })
    }
  }, [])

  // ── Initialization ────────────────────────────────────────────────

  useEffect(() => {
    if (!router.isReady) {
      return
    }
    // Re-run when storageKey changes scope (e.g. active org/workspace
    // changed) even though we already initialized once for a prior scope —
    // otherwise state hydrated for the old scope would keep leaking into
    // the new one until this hook's owner happens to unmount.
    const keyChanged = initializedForKeyRef.current !== storageKey
    if (hasInitializedRef.current && !keyChanged) {
      return
    }
    hasInitializedRef.current = true
    initializedForKeyRef.current = storageKey

    const urlState = parseUrlParams(router.query, schemaRef.current)
    const hasUrlParams = Object.keys(urlState).length > 0

    if (hasUrlParams) {
      // URL wins — merge with defaults for any fields not in the URL
      const resolved = { ...defaultsRef.current, ...urlState } as T
      setState(resolved)
      saveToStorage(resolved)
      // Mark as synced so the ongoing effect doesn't fire a redundant debounced sync
      lastSyncedStateRef.current = resolved
    } else {
      // No URL params — hydrate from localStorage under the current scope
      // (not defaults/prior state, which the closure would stale-capture
      // on a rescope) and sync it to URL.
      const hydrated = hydrateFromStorage(storageKey)
      setState(hydrated)
      syncToUrl(hydrated, true)
      lastSyncedStateRef.current = hydrated
    }

    setIsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy dependency suppression pending refactor
  }, [router.isReady, storageKey])

  // ── Ongoing sync (after initialization) ───────────────────────────

  const isReadyRef = useRef(false)
  isReadyRef.current = isReady

  // Use a ref to track the last state we synced to avoid re-syncing init state
  const lastSyncedStateRef = useRef<T | null>(null)

  useEffect(() => {
    if (!isReady) {
      return
    }

    // Skip if this is the exact state we just synced during init
    if (lastSyncedStateRef.current === state) {
      return
    }
    lastSyncedStateRef.current = state

    saveToStorage(state)
    syncToUrl(state)
  }, [state, isReady, saveToStorage, syncToUrl])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [])

  // ── Public API ────────────────────────────────────────────────────

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setState((prev) => ({ ...prev, [key]: value }))
  }, [])

  const setFields = useCallback((partial: Partial<T>) => {
    setState((prev) => ({ ...prev, ...partial }))
  }, [])

  const updateField = useCallback(
    <K extends keyof T>(key: K, updater: (prev: T[K]) => T[K]) => {
      setState((prev) => ({ ...prev, [key]: updater(prev[key]) }))
    },
    [],
  )

  const resetAll = useCallback(
    (options?: { overrides?: Partial<T>; removeUrlKeys?: string[] }) => {
      const nextState = options?.overrides
        ? { ...defaultsRef.current, ...options.overrides }
        : defaultsRef.current

      // Skip the ongoing effect so reset has one URL write, including external constraints.
      lastSyncedStateRef.current = nextState
      setState(nextState)

      const key = storageKeyRef.current
      if (options?.overrides) {
        saveToStorage(nextState)
      } else if (key) {
        try {
          localStorage.removeItem(key)
        } catch {
          // Storage access may be unavailable.
        }
      }

      syncToUrl(nextState, true, options?.removeUrlKeys)
    },
    [saveToStorage, syncToUrl],
  )

  return { state, setField, setFields, updateField, resetAll, isReady }
}
